import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FadeUp, MaskedLines, Eyebrow } from "@/components/Reveal";
import AppStoreButton from "@/components/AppStoreButton";
import Marquee from "@/components/Marquee";

const MARQUEE_ITEMS = [
  "Daha az seçenek. Daha anlamlı eşleşmeler.",
  "The Five",
  "Çöpçatan Desteği",
  "My Date",
  "Swipe değil, seçim.",
  "Sana özel seçilen eşleşmeler",
];

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden flex flex-col bg-[#1a0508] text-ivory-light">
      <motion.picture style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 will-change-transform" aria-hidden="true">
        <source media="(min-width: 768px)" srcSet="/hero/theatre-wide.jpg" />
        <img
          src="/hero/theatre-portrait.jpg"
          alt=""
          data-testid="hero-theatre-image"
          className="w-full h-full object-cover object-[65%_center] md:object-center"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </motion.picture>
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-[#1a0508]/80 via-[#1a0508]/30 to-transparent" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-[#1a0508]/50 via-transparent to-[#1a0508]/70" />
      <div aria-hidden="true" className="absolute inset-0 spot-flicker mix-blend-screen bg-[radial-gradient(ellipse_at_55%_45%,rgba(255,170,150,0.14),transparent_50%)]" />

      <div className="relative max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-12 items-center pt-36 sm:pt-44 pb-16 min-h-[92vh]">
        <div className="lg:col-span-7 relative z-10">
          <FadeUp>
            <Eyebrow light>Five Star · Concierge Dating</Eyebrow>
          </FadeUp>
          <h1 className="mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-ivory-light max-w-xl drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)]">
            <MaskedLines
              immediate
              delay={0.15}
              lines={[
                "Sonsuz profiller değil.",
                "Sana özel seçilen",
                <em key="em" className="italic text-rose-soft">
                  eşleşmeler.
                </em>,
              ]}
            />
          </h1>
          <FadeUp delay={0.6} className="mt-8 max-w-md text-base sm:text-lg font-light text-ivory/80 leading-relaxed">
            Five Star, sana özel seçilen eşleşmeleri, süreç boyunca çöpçatan desteğini ve geri bildirimlerini bir araya
            getiren kişisel bir dating deneyimidir.
          </FadeUp>
          <FadeUp delay={0.75} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-5">
            <AppStoreButton variant="light" testid="hero-appstore-button" />
            <Link to="/nasil-calisir" data-testid="hero-how-it-works-link" className="link-ghost-light">
              Nasıl Çalışır?
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </FadeUp>
          <FadeUp delay={0.9} className="mt-14 flex items-center gap-4 text-xs text-ivory/60 tracking-wide">
            <span className="h-px w-10 bg-gold/60" aria-hidden="true" />
            Swipe değil, seçim.
          </FadeUp>
        </div>
      </div>

      <Marquee items={MARQUEE_ITEMS} dark className="relative z-10 bg-[#1a0508]/70 backdrop-blur-sm" />
    </section>
  );
}
